import * as React from 'react';
import useRestService from '../shared/rest-service';
import HelpDocument from '../models/helpDocumentManagement';
import Tag from '../models/tag';
import useTag from './useTag';
import useDashboardManagement from './useDashboardManagement';
import Dashboard from '../models/dashboard';
import { HelpDocumentsContext } from '../shared/help-documents-context';
import useTimedAlerts from './useTimedAlerts';

/**
 * @function useHelpDocumentManagement - Custom hook for the document management page
 */
const useHelpDocumentManagement = () => {
  const { successMessage, dispatch, activeDocumentId } = React.useContext(HelpDocumentsContext);
  const { errorMessage, successMessage: message, setSuccessMessage } = useTimedAlerts();
  const { getAuth, postAuth, postFileAuth, putAuth } = useRestService();

  React.useEffect(() => {
    dispatch({ successMessage: message });
  }, [message]);

  // Clears success message after interval if success message is present on page load
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (successMessage) {
        dispatch({ successMessage: '' });
      }
    }, 5000);
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [successMessage]);

  /**
   * @function fetchGlobalDocument - Fetches a global document
   * @param documentId - the snippetId of the document to fetch
   */
  const fetchGlobalDocument = (documentId: number) => {
    return getAuth(`api/help-documents/${documentId}`);
  };

  /**
   * @function fetchClientDocument - Fetches a client document
   * @param clientId - Unique identifier for the client
   * @param documentId - the snippetId of the document to fetch
   */
  const fetchClientDocument = (clientId: number, documentId: number) => {
    return getAuth(`api/clients/${clientId}/help-documents/${documentId}`);
  };

  /**
   * @function fetchGlobalDocumentFromClient - Fetches a client document
   * @param clientId - Unique identifier for the client
   * @param documentId - the snippetId of the document to fetch
   */
  const fetchGlobalDocumentFromClient = (clientId: number, documentId: number) => {
    return getAuth(`api/clients/${clientId}/help-documents/global/${documentId}`);
  };

  /**
   * @function saveGlobalDocument - Create or save a new global document
   * @param document - the new document to create
   */
  const saveGlobalDocument = (document: HelpDocument) => {
    return postAuth('api/help-documents', document);
  };

  /**
   * @function saveClientDocument - Create or save a new client document
   * @param document - the new document to create
   */
  const saveClientDocument = (document: HelpDocument, clientId: string) => {
    return postAuth(`api/clients/${clientId}/help-documents`, document);
  };

  /**
   * @function publishGlobalDocument - Publish a revision of a global document
   * @param documentId - the snippetId of the document to publish
   */
  const publishGlobalDocument = (documentId: number) => {
    return putAuth(`api/help-documents/${documentId}/publish`, '');
  };

  /**
   * @function publishClientDocument - Publish a revision of a client document
   * @param clientId - the id of the client that owns the document
   * @param documentId - the snippetId of the document to publish
   */
  const publishClientDocument = (clientId: number, documentId: number) => {
    return putAuth(`api/clients/${clientId}/help-documents/${documentId}/publish`, '');
  };

  /**
   * @function uploadDocumentImage - Uploade an image for a document
   * @param image - the image file to upload
   */
  const uploadDocumentImage = (image: File) => {
    return postFileAuth('api/help-documents/image-upload', null, image);
  };

  const [loadingAssociatedData, setLoadingAssociatedData] = React.useState<boolean>(true);
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [dashboards, setDashboards] = React.useState<Dashboard[]>([]);
  const { fetchTags } = useTag();
  const { fetchAllDashboards } = useDashboardManagement();

  /**
   * @function fetchAssociatedData - Get extra data needed to create/edit a document
   */
  const fetchAssociatedData = (clientId: number) => {
    if (!loadingAssociatedData) setLoadingAssociatedData(true);
    Promise.all([fetchTags(clientId), fetchAllDashboards(clientId)]).then(
      ([tags, dashboards]: [Tag[], { results: Dashboard[]; totalItems: number }]) => {
        setTags(tags);
        setDashboards(dashboards.results);
        setLoadingAssociatedData(false);
      }
    );
  };

  return {
    fetchGlobalDocument,
    fetchClientDocument,
    fetchGlobalDocumentFromClient,
    saveGlobalDocument,
    saveClientDocument,
    publishGlobalDocument,
    publishClientDocument,
    uploadDocumentImage,
    fetchAssociatedData,
    loadingAssociatedData,
    tags,
    dashboards,
    successMessage,
    errorMessage,
    setSuccessMessage,
    activeDocumentId
  };
};

export default useHelpDocumentManagement;
